/**
 * Depot code → full name lookup, fetched once from `/api/depots` and cached
 * across the whole admin SPA.
 *
 * Usage:
 *   const depots = useDepots();           // { "PNJ": "Panaji", ... }
 *   <span>{fmtDepot(b.depot, depots)}</span>
 *
 * Falls back to the raw code if no depot row exists (e.g. an unimported one).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

let _cache = null;          // shared module-level cache
let _inflight = null;       // de-dupe parallel fetches
const _subscribers = new Set();

const _notify = () => _subscribers.forEach((cb) => cb(_cache));

async function _load() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = api.get("/depots").then(({ data }) => {
    const map = {};
    (data || []).forEach((d) => {
      if (d?.code) map[d.code] = d.name || d.code;
    });
    _cache = map;
    _notify();
    _inflight = null;
    return map;
  }).catch(() => {
    _cache = {};
    _inflight = null;
    return _cache;
  });
  return _inflight;
}

/** React hook — returns a {code: name} object. Re-renders on first load. */
export function useDepots() {
  const [map, setMap] = useState(_cache || {});
  useEffect(() => {
    _subscribers.add(setMap);
    if (!_cache) _load();
    else setMap(_cache);
    return () => _subscribers.delete(setMap);
  }, []);
  return map;
}

/** Pure formatter — resolve a single depot code to its full name. */
export function fmtDepot(code, map) {
  if (!code) return "—";
  if (!map) return code;
  return map[code] || code;
}

/** Manually flush the cache (e.g. after editing a depot). */
export function refreshDepots() {
  _cache = null;
  return _load();
}
